import { HolderOutlined } from "@ant-design/icons";
import { Modifier } from "@dnd-kit/core";
import { Button } from "antd";
import { createContext, HTMLAttributes, useContext } from "react";
import { useSortableRow } from "./HelpCentre/editor/Sortable";

/*
 * Drag-to-reorder rows for an antd Table inside a SortableList (Ant Design's
 * "drag sorting with handle" pattern). Each row registers as a sortable item
 * and hands its drag-handle props to the handle cell through context, so only
 * the grip starts a drag and clicks on the row's links still work.
 */

type HandleProps = ReturnType<typeof useSortableRow>["handleProps"];

const RowHandleContext = createContext<HandleProps | null>(null);

type RowProps = HTMLAttributes<HTMLTableRowElement> & {
	"data-row-key"?: string;
};

function SortableDataRow({
	rowKey,
	...props
}: Readonly<RowProps & { rowKey: string }>) {
	const { setNodeRef, style, isDragging, handleProps } = useSortableRow(rowKey);
	return (
		<RowHandleContext.Provider value={handleProps}>
			<tr
				{...props}
				ref={setNodeRef}
				style={{ ...props.style, ...style }}
				className={
					isDragging ? `${props.className ?? ""} is-dragging` : props.className
				}
			/>
		</RowHandleContext.Provider>
	);
}

function SortableTableRow(props: Readonly<RowProps>) {
	const rowKey = props["data-row-key"];
	// antd also renders its "No data" row through this, without a key.
	if (rowKey === undefined) {
		return <tr {...props} />;
	}
	return <SortableDataRow rowKey={String(rowKey)} {...props} />;
}

/** For the Table's `components` prop. */
export const sortableTableComponents = { body: { row: SortableTableRow } };

/** Table rows move up and down only (as @dnd-kit/modifiers' restrictToVerticalAxis). */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
	...transform,
	x: 0,
});

/** For the SortableList's `modifiers` prop. */
export const sortableTableModifiers = [restrictToVerticalAxis];

/** The grip in a sortable row: drag with the pointer, or Space and the arrow keys. */
function TableDragHandle({ label }: Readonly<{ label: string }>) {
	const handleProps = useContext(RowHandleContext);
	if (!handleProps) {
		return null;
	}
	const { ref, ...handle } = handleProps;
	// When reordering is off, dnd-kit sets aria-disabled and drops the
	// listeners. No native `disabled`: it would blur the handle while the order
	// saves, losing a keyboard user's place.
	return (
		<Button
			{...handle}
			ref={ref}
			type="text"
			size="small"
			className="table-drag-handle"
			icon={<HolderOutlined aria-hidden="true" />}
			aria-label={label}
		/>
	);
}

/** A first column with each row's drag handle, named after the row's `nameField`. */
export const dragHandleColumn = (nameField: string) => ({
	key: "reorder",
	width: 48,
	render: (_: unknown, record: Record<string, any>) => (
		<TableDragHandle label={`Reorder ${record[nameField]}`} />
	),
});
